import { isSystemRemote, systemText, systemErrorFailure, type SystemRemote } from "./systemFeedbackPresentation";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import messages from "./locales/en/systemOperations";

export type OperationsKey = Exclude<keyof typeof messages, `${string}_one` | `${string}_other`>;
export type OperationsTranslate = TFunction<"systemOperations">;
export type OperationFailure = string | SystemRemote | { key: OperationsKey; values?: Record<string, string | number> };

/** Resolve asynchronous feedback in the current language without replacing request callbacks. */
export function useSystemOperations() {
  const { i18n } = useTranslation("systemOperations");
  const op = useMemo(() => i18n.getFixedT(null, "systemOperations"), [i18n]);
  return { op, locale: i18n.resolvedLanguage ?? i18n.language };
}

export function operationFailureText(value: OperationFailure | null, op: OperationsTranslate): string | null {
  return value === null ? null : typeof value === "string" ? value : isSystemRemote(value) ? systemText(value) : op(($): string => $[value.key], value.values);
}

export class SystemOperationError extends Error {
  constructor(readonly key: OperationsKey, readonly values?: Record<string, string | number>, original?: string) {
    super(original ?? messages[key].replace(/{{(\w+)}}/g, (_, name: string) => String(values?.[name] ?? `{{${name}}}`)));
  }
}

export function operationFailure(error: unknown, fallback: OperationsKey): OperationFailure {
  const remote = systemErrorFailure(error); if (remote) return remote;
  if (error instanceof SystemOperationError) return { key: error.key, values: error.values };
  return error instanceof Error ? error.message : { key: fallback };
}

export function durationUnits(parts: Array<[number, "day" | "hour" | "minute" | "second"]>, locale: string): string {
  return parts.map(([value, unit]) => new Intl.NumberFormat(locale, { style: "unit", unit, unitDisplay: "narrow" }).format(value)).join(" ");
}

export function operationUptime(seconds: number, locale: string): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return durationUnits(days > 0 ? [[days, "day"], [hours, "hour"]] : hours > 0 ? [[hours, "hour"], [minutes, "minute"]] : [[minutes, "minute"]], locale);
}

/** Keeps scheduling arithmetic separate from locale-dependent presentation. */
export function networkScheduleTime(unixSec: number | null | undefined, nowMs: number, future: boolean, op: OperationsTranslate, locale: string): string {
  if (typeof unixSec !== "number" || !Number.isFinite(unixSec)) return future ? "—" : op($ => $.never);
  const seconds = future ? Math.round(unixSec - nowMs / 1000) : Math.max(0, Math.round(nowMs / 1000 - unixSec));
  if (future && seconds <= 0) return op($ => $.now);
  let parts: Array<[number, "day" | "hour" | "minute" | "second"]>;
  if (seconds < 60) parts = [[seconds, "second"]];
  else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60), rest = seconds % 60;
    parts = rest > 0 || future ? [[minutes, "minute"], [rest, "second"]] : [[minutes, "minute"]];
  } else if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600), minutes = Math.round((seconds % 3600) / 60);
    parts = minutes > 0 ? [[hours, "hour"], [minutes, "minute"]] : [[hours, "hour"]];
  } else {
    const days = Math.floor(seconds / 86400), hours = Math.round((seconds % 86400) / 3600);
    parts = hours > 0 ? [[days, "day"], [hours, "hour"]] : [[days, "day"]];
  }
  const duration = durationUnits(parts, locale);
  return future ? op($ => $.inDuration, { duration }) : op($ => $.agoDuration, { duration });
}

export function networkRelativeTime(unixSec: number | null | undefined, locale: string, op: OperationsTranslate): string {
  if (typeof unixSec !== "number" || !Number.isFinite(unixSec)) return op($ => $.never);
  const delta = Math.floor(Date.now() / 1000) - unixSec;
  const seconds = Math.abs(delta);
  const divisor = seconds < 60 ? 1 : seconds < 3600 ? 60 : seconds < 86400 ? 3600 : 86400;
  const unit = divisor === 1 ? "second" : divisor === 60 ? "minute" : divisor === 3600 ? "hour" : "day";
  return new Intl.RelativeTimeFormat(locale, { numeric: "always", style: "short" }).format((delta < 0 ? 1 : -1) * Math.round(seconds / divisor), unit);
}
